import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Typography,
  Row,
  Col,
  Switch,
  Avatar,
  Spin,
  Modal,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  EnvironmentOutlined,
  BankOutlined,
  TrophyOutlined,
  BellOutlined,
  SaveOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import API from "../../services/api";
import "./PersonalDetails.css";

const { Title, Text } = Typography;

function PersonalDetails() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [userName, setUserName] = useState("");
  const [imgError, setImgError] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  const backendBaseUrl = "http://localhost:5000";

  const getFullImageUrl = (imgPath) => {
    if (!imgPath || imgPath.trim() === "") return null;
    if (imgPath.startsWith("http")) return imgPath;
    return `${backendBaseUrl}${imgPath}`;
  };

  const fetchProfile = async () => {
    try {
      setFetching(true);
      const { data } = await API.get("/users/profile");

      const profileImg = data.profileImg ? getFullImageUrl(data.profileImg) : null;
      setImageUrl(profileImg);
      setUserName(data.name || "");

      const formValues = {
        name: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        companyName: data.companyName || "",
        designation: data.designation || "",
        city: data.address?.city || "",
        state: data.address?.state || "",
        country: data.address?.country || "",
        website: data.socialLinks?.website || "",
        linkedin: data.socialLinks?.linkedin || "",
        emailAlerts: data.notificationPreferences?.emailAlerts ?? true,
        marketingEmails: data.notificationPreferences?.marketingEmails ?? false,
      };

      setInitialValues(formValues);
      form.setFieldsValue(formValues);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to load profile");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Track form changes
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    const hasChanges = JSON.stringify(currentValues) !== JSON.stringify(initialValues);
    setIsFormDirty(hasChanges);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        companyName: values.companyName,
        designation: values.designation,
        address: {
          city: values.city,
          state: values.state,
          country: values.country,
        },
        socialLinks: {
          website: values.website,
          linkedin: values.linkedin,
        },
        notificationPreferences: {
          emailAlerts: values.emailAlerts,
          marketingEmails: values.marketingEmails,
        },
      };

      const { data } = await API.put("/users/profile", payload);

      localStorage.setItem(
        "user",
        JSON.stringify({
          ...data,
          profileImg: imageUrl || data.profileImg || null,
        })
      );

      message.success("Profile updated successfully");
      setIsFormDirty(false);
      setTimeout(() => navigate(-1), 500);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (isFormDirty) {
      Modal.confirm({
        title: "Unsaved Changes",
        icon: <ExclamationCircleOutlined />,
        content: "You have unsaved changes. Are you sure you want to leave? Your changes will be lost.",
        okText: "Leave",
        cancelText: "Stay",
        okButtonProps: { danger: true },
        onOk: () => {
          navigate(-1);
        },
      });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="pd">
      <div className="pd__page">
        <div className="pd__header">
          <div className="pd__header-left">
           
            <Title level={2} className="pd__h1">
              Personal Details
            </Title>
            <Text className="pd__sub">
              Manage your personal information, location, social links, and notification preferences.
            </Text>
          </div>
        </div>

        <Card className="pd__card" variant={false}>
          {/* Close Button */}
          <button className="pd__close-btn" onClick={handleClose}>
            <CloseOutlined />
          </button>

          <div className="pd__card-top">
            <div className="pd__profile">
              <div className="pd__avatar-wrap">
                {fetching ? (
                  <div className="pd__spinner">
                    <Spin size="large" />
                  </div>
                ) : (
                  <Avatar
                    className="pd__avatar"
                    size={100}
                    src={!imgError && imageUrl ? imageUrl : undefined}
                    onError={() => {
                      setImgError(true);
                      return false;
                    }}
                    style={!imageUrl || imgError ? { backgroundColor: "#ff3f6c", color: "#fff" } : {}}
                  >
                    {(!imageUrl || imgError) && (userName?.charAt(0)?.toUpperCase() || "U")}
                  </Avatar>
                )}
              </div>

              <div className="pd__profile-copy">
                <h3 className="pd__profile-title">Profile Information</h3>
                <p className="pd__profile-text">
                  Keep your profile updated so your account details and preferences stay accurate across the platform.
                </p>
              </div>
            </div>
          </div>

          <Form
            className="pd__form"
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={handleFormChange}
            initialValues={{
              emailAlerts: true,
              marketingEmails: false,
            }}
          >
            {/* Basic Details Section */}
            <div className="pd__section">
              <div className="pd__section-head">
                <div className="pd__section-icon">
                  <UserOutlined />
                </div>
                <h4 className="pd__section-title">Basic Details</h4>
                <div className="pd__section-line" />
              </div>

              <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter your name" }]}
                  >
                    <Input size="large" placeholder="Enter full name" disabled prefix={<UserOutlined />} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input size="large" placeholder="Enter email" disabled prefix={<MailOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Phone Number"
                    name="phone"
                    rules={[
                      {
                        pattern: /^[0-9]{10}$/,
                        message: "Phone number must be 10 digits",
                      },
                    ]}
                  >
                    <Input size="large" placeholder="Enter phone number" prefix={<PhoneOutlined />} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="Company Name" name="companyName">
                    <Input size="large" placeholder="Enter company name" prefix={<BankOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 0]}>
                <Col xs={24}>
                  <Form.Item label="Designation" name="designation">
                    <Input size="large" placeholder="Enter designation" prefix={<TrophyOutlined />} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Location Section */}
            <div className="pd__section">
              <div className="pd__section-head">
                <div className="pd__section-icon">
                  <EnvironmentOutlined />
                </div>
                <h4 className="pd__section-title">Location</h4>
                <div className="pd__section-line" />
              </div>

              <Row gutter={[24, 0]}>
                <Col xs={24} md={8}>
                  <Form.Item label="City" name="city">
                    <Input size="large" placeholder="Enter city" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="State" name="state">
                    <Input size="large" placeholder="Enter state" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item label="Country" name="country">
                    <Input size="large" placeholder="Enter country" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Social Links Section */}
            <div className="pd__section">
              <div className="pd__section-head">
                <div className="pd__section-icon">
                  <GlobalOutlined />
                </div>
                <h4 className="pd__section-title">Social & Web</h4>
                <div className="pd__section-line" />
              </div>

              <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Website" name="website">
                    <Input size="large" placeholder="https://example.com" prefix={<GlobalOutlined />} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label="LinkedIn" name="linkedin">
                    <Input size="large" placeholder="https://linkedin.com/in/username" prefix={<LinkedinOutlined />} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Notifications Section */}
            <div className="pd__section">
              <div className="pd__section-head">
                <div className="pd__section-icon">
                  <BellOutlined />
                </div>
                <h4 className="pd__section-title">Notifications</h4>
                <div className="pd__section-line" />
              </div>

              <div className="pd__switch-grid">
                <div className="pd__switch-card">
                  <div className="pd__switch-card-content">
                    <Form.Item name="emailAlerts" valuePropName="checked" noStyle>
                      <Switch className="pd__switch" />
                    </Form.Item>
                    <div className="pd__switch-info">
                      <span className="pd__switch-label">Email Alerts</span>
                      <span className="pd__switch-desc">Receive important updates via email</span>
                    </div>
                  </div>
                </div>

                <div className="pd__switch-card">
                  <div className="pd__switch-card-content">
                    <Form.Item name="marketingEmails" valuePropName="checked" noStyle>
                      <Switch className="pd__switch" />
                    </Form.Item>
                    <div className="pd__switch-info">
                      <span className="pd__switch-label">Marketing Emails</span>
                      <span className="pd__switch-desc">Get offers, news, and inspiration</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pd__actions">
              <Button type="primary" htmlType="submit" size="large" loading={loading} icon={<SaveOutlined />}>
                Save Changes
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default PersonalDetails;